// Hook para sincronização instantânea de promoções - Sistema Virtual DOM
import { useEffect, useCallback, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

interface PromoSnapshot {
  id: string;
  nome: string;
  modalStatus: string;
  active: boolean;
  start_time: string;
  end_time: string;
  interval: number;
  duration: number;
  timestamp: number;
  checksum: string;
}

interface UseInstantPromoSyncProps {
  userId: string;
  screenId: string;
  onPromoChanged: () => void;
}

export const useInstantPromoSync = ({ userId, screenId, onPromoChanged }: UseInstantPromoSyncProps) => {
  const [promoVirtualDOM, setPromoVirtualDOM] = useState<Map<string, PromoSnapshot>>(new Map());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastSyncRef = useRef<number>(0);

  // Função para criar checksum de uma promoção
  const createPromoChecksum = useCallback((promo: any): string => {
    const data = `${promo.id}-${promo.nome}-${promo['Modal visível']}-${promo.active}-${promo.start_time}-${promo.end_time}-${promo.interval}-${promo.duration}`;
    return btoa(data); // Base64 encode como checksum simples
  }, []);

  // Sincronização instantânea com o banco
  const syncPromotions = useCallback(async () => {
    if (!userId) return;

    try {
      console.log('🔄 [INSTANT SYNC] Sincronizando promoções...');
      
      const { data: promos, error } = await supabase
        .from('promos')
        .select('*, "Modal visível", campaign_id')
        .eq('user_id', userId)
        .eq('active', true);

      if (error) {
        console.error('❌ [INSTANT SYNC] Erro ao buscar promoções:', error);
        return;
      }

      if (!promos) return;

      let hasChanges = false;

      // Verificar mudanças em cada promoção
      for (const promo of promos) {
        const newChecksum = createPromoChecksum(promo);
        const currentSnapshot = promoVirtualDOM.get(promo.id);

        if (!currentSnapshot || currentSnapshot.checksum !== newChecksum) {
          console.log(`🚀 [INSTANT SYNC] Mudança detectada em ${promo.nome}:`, {
            old: currentSnapshot?.checksum,
            new: newChecksum,
            changes: {
              modalStatus: promo['Modal visível'],
              active: promo.active,
              start_time: promo.start_time,
              end_time: promo.end_time,
              interval: promo.interval,
              duration: promo.duration
            }
          });

          const newSnapshot: PromoSnapshot = {
            id: promo.id,
            nome: promo.nome,
            modalStatus: promo['Modal visível'],
            active: promo.active,
            start_time: promo.start_time,
            end_time: promo.end_time,
            interval: promo.interval,
            duration: promo.duration,
            timestamp: Date.now(),
            checksum: newChecksum
          };

          setPromoVirtualDOM(prev => {
            const newMap = new Map(prev);
            newMap.set(promo.id, newSnapshot);
            return newMap;
          });

          hasChanges = true;
        }
      }

      // Detectar promoções removidas
      for (const [promoId] of promoVirtualDOM) {
        if (!promos.find(p => p.id === promoId)) {
          console.log(`🗑️ [INSTANT SYNC] Promoção removida: ${promoId}`);
          setPromoVirtualDOM(prev => {
            const newMap = new Map(prev);
            newMap.delete(promoId);
            return newMap;
          });
          hasChanges = true;
        }
      }

      if (hasChanges) {
        console.log('⚡ [INSTANT SYNC] Mudanças detectadas - disparando callback');
        onPromoChanged();
        
        // Emitir evento global para outros componentes
        window.dispatchEvent(new CustomEvent('instantPromoSync', {
          detail: {
            type: 'promotions_changed',
            timestamp: Date.now(),
            userId,
            screenId
          }
        }));
      }

      lastSyncRef.current = Date.now();

    } catch (error) {
      console.error('❌ [INSTANT SYNC] Erro na sincronização:', error);
    }
  }, [userId, promoVirtualDOM, createPromoChecksum, onPromoChanged, screenId]);

  // Configurar sincronização automática
  useEffect(() => {
    if (!userId) return;

    console.log('🚀 [INSTANT SYNC] Iniciando sistema de sincronização instantânea');

    // Sincronização inicial
    syncPromotions();

    // Polling otimizado - reduzido para 3 segundos (menos agressivo)
    intervalRef.current = setInterval(syncPromotions, 3000);

    // Configurar Realtime listener otimizado
    const channelName = `instant-promo-sync-${userId}`;
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'promos',
        filter: `user_id=eq.${userId}`
      }, (payload) => {
        console.log('🔄 [INSTANT SYNC] Realtime change detected:', {
          event: payload.eventType,
          table: payload.table,
          id: payload.new?.id || payload.old?.id,
          nome: payload.new?.nome || payload.old?.nome
        });
        
        // Evitar sincronização dupla - verificar se já sincronizou recentemente
        const timeSinceLastSync = Date.now() - lastSyncRef.current;
        if (timeSinceLastSync > 500) { // Mínimo 500ms entre sincronizações
          setTimeout(syncPromotions, 200);
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ [INSTANT SYNC] Realtime conectado com sucesso');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ [INSTANT SYNC] Erro na conexão Realtime');
        }
      });

    console.log('🚀 [INSTANT SYNC] Sistema de sincronização instantânea ativado');

    return () => {
      console.log('🛑 [INSTANT SYNC] Desativando sistema de sincronização...');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      
      // Remover canal com callback para confirmar
      supabase.removeChannel(channel).then(() => {
        console.log('🔌 [INSTANT SYNC] Canal Realtime removido com sucesso');
      });
    };
  }, [userId]); // Dependência simplificada

  return {
    promoVirtualDOM,
    syncPromotions,
    lastSync: lastSyncRef.current
  };
};